import { useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';

/**
 * Lays a grid's children out as an ordered masonry: each card goes, in DOM order, into the column
 * that is shortest so far, so there are no holes under short cards and reading order stays the DOM
 * order. A child with data-wide spans two columns, at the pair that is free highest; with data-full
 * as well it spans every column and starts below all of them. Without
 * ResizeObserver the grid keeps its CSS layout.
 */
export function useOrderedMasonry(
  ref: RefObject<HTMLElement | null>,
  minColumnWidth: number,
  maxColumns: number
): void {
  useLayoutEffect(() => {
    const grid = ref.current;
    if (!grid || typeof ResizeObserver === 'undefined') return undefined;
    const cards = () => Array.from(grid.children) as HTMLElement[];
    let frame = 0;

    const layout = () => {
      frame = 0;
      const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
      const columns = Math.max(
        1,
        Math.min(maxColumns, Math.floor((grid.clientWidth + gap) / (minColumnWidth + gap)))
      );
      // Rows of 1px, so a card's span is its height and its start is the column's height so far
      grid.style.gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`;
      grid.style.gridAutoRows = '1px';
      grid.style.rowGap = '0';
      const heights = new Array<number>(columns).fill(0);
      for (const card of cards()) {
        const span = Math.ceil(card.getBoundingClientRect().height + gap);
        if (card.dataset.full !== undefined) {
          const top = Math.max(...heights);
          card.style.gridColumn = '1 / -1';
          card.style.gridRow = `${top + 1} / span ${span}`;
          heights.fill(top + span);
        } else if (card.dataset.wide !== undefined && columns > 1) {
          let start = 0;
          for (let column = 1; column < columns - 1; column++)
            if (Math.max(heights[column], heights[column + 1]) < Math.max(heights[start], heights[start + 1]))
              start = column;
          const top = Math.max(heights[start], heights[start + 1]);
          card.style.gridColumn = `${start + 1} / span 2`;
          card.style.gridRow = `${top + 1} / span ${span}`;
          heights[start] = heights[start + 1] = top + span;
        } else {
          const column = heights.indexOf(Math.min(...heights));
          card.style.gridColumn = `${column + 1}`;
          card.style.gridRow = `${heights[column] + 1} / span ${span}`;
          heights[column] += span;
        }
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(layout);
    };

    const sizes = new ResizeObserver(schedule);
    const watch = () => {
      sizes.disconnect();
      sizes.observe(grid);
      cards().forEach((card) => sizes.observe(card));
    };
    const children = new MutationObserver(() => {
      watch();
      schedule();
    });
    children.observe(grid, { childList: true, subtree: true, attributeFilter: ['data-full'] });
    watch();
    layout();

    return () => {
      cancelAnimationFrame(frame);
      sizes.disconnect();
      children.disconnect();
      for (const property of ['gridTemplateColumns', 'gridAutoRows', 'rowGap'] as const)
        grid.style[property] = '';
      for (const card of cards()) {
        card.style.gridColumn = '';
        card.style.gridRow = '';
      }
    };
  }, [ref, minColumnWidth, maxColumns]);
}

/** A grid laid out with useOrderedMasonry; mounting it (switching to Cards) lays it out. */
export function OrderedMasonry({
  className,
  minColumnWidth,
  maxColumns,
  children,
}: {
  className?: string;
  minColumnWidth: number;
  maxColumns: number;
  children: ReactNode;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useOrderedMasonry(ref, minColumnWidth, maxColumns);
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
